<template>
  <div class="dynamic-test-form">
    <div v-if="!Comp" class="dtf-unsupported">
      <p>La corrección de este test aún no está disponible aquí.</p>
    </div>
    <component
      v-else
      :is="Comp"
      ref="form"
      :key="testValue"
      :test_id="testValue"
      :student_id="null"
      :previous_answer="previousAnswer || {}"
      :semester="1"
      @add-test="onAddTest"
      @update-document="onUpdateDocument"
    />
  </div>
</template>

<script>
// Renders the existing form-test component for the selected test value —
// same UI the regular test creator uses, no schema flattening — and routes
// its submissions through the studentless endpoint by monkey-patching the
// services it imports for the lifetime of this wrapper.
//
// The form-test components were written for the student-coupled flow:
// they call testService.send_test (which expects a student or makes a
// default one) and follow up with redpieService.assignDocuments /
// checkTestDocumentsStarted (both student-grade-coupled). Modifying every
// component would be invasive; instead we replace the methods on the
// service singletons while this wrapper is mounted, then restore them on
// destroy. Because every importer of those services references the same
// default-export object, the override is seen by all of them.
import testService from "@/services/testService";
import redpieService from "@/services/redpieService";
import { withCorrectionContext } from "@/services/testCorrectionAdapter";
// TEPROSIF and TECAL use chatbot-specific redesigns (card layout); the regular
// test creator still uses the originals via ModalAutomaticTest.
import TeprosifChatbot from "@/components/redpie/miscellaneous/form-test/TeprosifChatbot";
import TecalChatbot from "@/components/redpie/miscellaneous/form-test/TecalChatbot";
import {
  Stsg,
  Idtel,
  Pefe,
  Wais4,
  Wisc,
  Wisc5,
  Evalua,
  Evalua4,
  Evamat,
  Clpt,
  Precalculo,
  Funciones,
  Icap2,
  Sensorial,
  SensorialEscolar,
} from "@/components/redpie/miscellaneous/form-test";

// TestEnum value -> form-test component. Mirror of ModalAutomaticTest.vue
// (ICAP value 15 uses the Icap2 component; ICAP value 10 is legacy).
const TEST_COMPONENT_BY_VALUE = {
  3: TeprosifChatbot,
  5: Stsg,
  7: Idtel,
  8: Pefe,
  9: TecalChatbot,
  0: Wais4,
  1: Wisc,
  15: Icap2,
  11: Wisc5,
  2: Evalua,
  12: Evalua4,
  4: Evamat,
  6: Clpt,
  13: Precalculo,
  14: Funciones,
  16: Sensorial,
  17: SensorialEscolar,
};

export default {
  name: "DynamicTestForm",
  props: {
    testValue: { type: Number, required: true },
    previousAnswer: { type: Object, required: false, default: () => ({}) },
  },
  computed: {
    Comp() {
      return TEST_COMPONENT_BY_VALUE[this.testValue] || null;
    },
  },
  data() {
    return { capturedResult: null };
  },
  mounted() {
    this._origSendTest = testService.send_test;
    this._origAssignDocuments = redpieService.assignDocuments;
    this._origCheckTestDocsStarted = redpieService.checkTestDocumentsStarted;

    // All test submissions go through the studentless endpoint. The shape
    // the form-test components expect from .then((data) => …) matches what
    // correctTestNoStudent returns (same TestSerializer payload, plus the
    // TEPROSIF step-0 extras and the IDTEL intermediate {redpsi_id, data}).
    //
    // prunePayload is intentionally NOT applied here. It was added for the
    // PEFE age-toggle case, but PEFE / PRECALCULO are paused
    // (see ChatbotTestCorrection.TEST_CATEGORIES), and the
    // schema-key walker can't capture the var names of tests with unusual
    // schema shapes (Tecal's `[var, label]` tuples is the concrete example
    // — filtering would drop p1..p101 and only keep `edad`). Resurrect
    // prunePayload if a future test re-introduces the toggle-stale-fields
    // problem and has a walkable schema.
    // withCorrectionContext es lo que vincula el Test al estudiante: los
    // componentes de form-test/ arman su payload con el student_id y el
    // semestre que reciben por prop, y acá abajo los dos van fijos (null y 1)
    // porque nacieron para el chatbot anónimo. Dentro del colegio el contexto
    // real lo tiene el adapter (setCorrectionStudent/setCorrectionSemester,
    // que el workspace sincroniza), así que sobreescribe ambos. Sin contexto
    // devuelve el payload intacto y el flujo a granel queda igual que antes.
    testService.send_test = (payload) =>
      testService.correctTestNoStudent(withCorrectionContext(payload));

    // Student-grade-coupled side effects that we don't want during the
    // chatbot flow. checkTestDocumentsStarted's normal response shape is
    // {started_documents: []} — return an empty list so IDTEL's
    // "documents already started" branch never triggers.
    redpieService.assignDocuments = () => Promise.resolve({});
    redpieService.checkTestDocumentsStarted = () =>
      Promise.resolve({ started_documents: [] });
  },
  beforeDestroy() {
    if (this._origSendTest) testService.send_test = this._origSendTest;
    if (this._origAssignDocuments) {
      redpieService.assignDocuments = this._origAssignDocuments;
    }
    if (this._origCheckTestDocsStarted) {
      redpieService.checkTestDocumentsStarted = this._origCheckTestDocsStarted;
    }
  },
  methods: {
    // PEFE's form swaps the rendered schema when the user toggles `edad`,
    // but doesn't clear answer.answers — so if the user touches both ages
    // the payload carries fields from both schemas, and RedPsi rejects on
    // the first unknown one ("Pefe() got an unexpected keyword argument
    // 'contenidos'"). Trim the payload to only the keys present in the
    // currently rendered schema before submitting. Other tests have a
    // single static schema, so we use schema-filter for them too — it's a
    // safe defensive cleanup.
    prunePayload(payload) {
      const form = this.$refs.form;
      if (!form || !payload || typeof payload !== "object") return payload;
      const schemaKeys = this.collectSchemaKeys(form);
      if (!schemaKeys.size || !payload.answers) return payload;
      const filtered = {};
      Object.keys(payload.answers).forEach((k) => {
        if (schemaKeys.has(k)) filtered[k] = payload.answers[k];
      });
      return { ...payload, answers: filtered };
    },
    // Walks form.test (or form.test[form.answer.step] for multi-step
    // schemas like IDTEL) and pulls every `var` reachable from the
    // currently rendered fields. Recognises both flat items, grouped
    // items (`elements`), and nested-section items (`questions`) so the
    // approach works for all the form-test components.
    collectSchemaKeys(form) {
      const keys = new Set();
      let schema = form && form.test;
      if (!schema) return keys;
      // IDTEL: schema is an array of step-arrays; only the current step
      // is visible at submit time. For everyone else, schema is a single
      // array; for TEPROSIF, schema is an object (no `var`s to collect —
      // skip and let the unfiltered payload through).
      if (
        Array.isArray(schema) &&
        schema.length &&
        Array.isArray(schema[0]) &&
        form.answer &&
        typeof form.answer.step === "number"
      ) {
        schema = schema[form.answer.step] || [];
      }
      if (!Array.isArray(schema)) return keys;
      const walk = (items) => {
        items.forEach((item) => {
          if (!item || typeof item !== "object") return;
          if (item.var) keys.add(item.var);
          if (Array.isArray(item.elements)) walk(item.elements);
          if (Array.isArray(item.questions)) walk(item.questions);
        });
      };
      walk(schema);
      return keys;
    },
    onAddTest(test) {
      // Multi-step components (IDTEL, TEPROSIF) only emit add-test on the
      // final step, so this is the right signal that the test is fully
      // corrected. `test` here is the TestSerializer dict (has .id).
      this.capturedResult = test;
      this.$emit("corrected", test);
    },
    onUpdateDocument() {
      // The form-test components emit update-document after assignDocuments
      // resolves. With assignDocuments stubbed, this still fires (because
      // the stub resolves) but we don't need to do anything with it.
    },
  },
};
</script>

<style scoped>
.dynamic-test-form {
  width: 100%;
}
.dtf-unsupported {
  padding: 12px;
  color: #6b7280;
  font-style: italic;
}
</style>
